import type { RecordingRawTranscriptFragment } from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Duration } from "luxon";
import type { FC, PropsWithChildren } from "react";
import { usePress } from "react-aria";
import { tv } from "tailwind-variants";
import { renderSpeakerName } from "./gradingHelpers.js";

export interface TranscriptFragmentData {
  recording: AccountGradingRecording;
  fragment: RecordingRawTranscriptFragment;
}

export const quoteStyle = tv({
  base: `
    rounded-md
    border
    p-2
    text-xs
    leading-relaxed
    transition-colors
    duration-200
  `,
  variants: {
    kind: {
      incomplete: `
        border-blue-50
        bg-blue-50
        font-mono
        text-blue-800
      `,
      plain: `
        border-gray-100
        bg-gray-100
        font-mono
        text-foreground
      `,
      clickable: `
        cursor-pointer
        border-blue-50
        bg-blue-50
        text-blue-800
        hover:border-blue-500
        hover:bg-blue-100
      `,
    },
  },
});

export const IncompleteQuote: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return <p className={quoteStyle({ kind: "incomplete" })}>"{children}"</p>;
};

export const Quote: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return <p className={quoteStyle({ kind: "plain" })}>"{children}"</p>;
};

interface ClickableQuoteProps extends PropsWithChildren {
  fragmentData: TranscriptFragmentData;
  onPress?: () => void;
}

export const ClickableQuote: FC<ClickableQuoteProps> = (props) => {
  const { fragmentData, onPress, children } = props;
  const { pressProps } = usePress({ onPress });
  return (
    <div
      {...pressProps}
      className={quoteStyle({ kind: "clickable" })}
      role="button"
      tabIndex={0}
    >
      <div className="float-right ml-2 flex flex-row gap-1 rounded-[4px] bg-white px-1.5">
        <div className="inline-block max-w-28 truncate font-semibold text-foreground">
          {renderSpeakerName(
            fragmentData.recording.speakers,
            fragmentData.fragment.speaker_id,
          )}
        </div>
        <div className="text-gray-600">
          {Duration.fromMillis(fragmentData.fragment.start_time).toFormat(
            "h:mm:ss",
          )}
        </div>
      </div>
      <span className="font-mono">"{children}"</span>
    </div>
  );
};
